/**
 * Note Utility Functions
 *
 * Utilities for working with notes, including:
 * - Plain text to Tiptap JSON conversion
 * - Plain text extraction from Tiptap JSON
 * - HTML generation from Tiptap JSON
 */

import type { JSONContent } from '@tiptap/core';
import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

/**
 * Convert plain text to Tiptap JSON format
 * Preserves paragraphs separated by blank lines
 */
export function convertPlainTextToTiptap(plainText: string): JSONContent {
  if (!plainText || plainText.trim() === '') {
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: []
        }
      ]
    };
  }

  // Split by double newlines for paragraphs
  const paragraphs = plainText.split('\n\n');

  const content: JSONContent[] = paragraphs.map(para => {
    // Handle single newlines within paragraphs as hard breaks
    const lines = para.split('\n');

    if (lines.length === 1) {
      // Single line paragraph
      return {
        type: 'paragraph',
        content: lines[0].trim() ? [{ type: 'text', text: lines[0] }] : []
      };
    }

    // Multi-line paragraph with hard breaks
    const paraContent: JSONContent[] = [];
    lines.forEach((line, index) => {
      if (line.trim()) {
        paraContent.push({ type: 'text', text: line });
      }
      if (index < lines.length - 1) {
        paraContent.push({ type: 'hardBreak' });
      }
    });

    return {
      type: 'paragraph',
      content: paraContent.length > 0 ? paraContent : []
    };
  });

  return {
    type: 'doc',
    content
  };
}

/**
 * Extract plain text from Tiptap JSON
 * Useful for search and preview
 */
export function extractPlainText(content: JSONContent): string {
  if (!content || typeof content !== 'object') {
    return '';
  }

  const getText = (node: JSONContent): string => {
    // Base case: text node
    if (node.type === 'text') {
      return node.text || '';
    }

    // Hard break becomes newline
    if (node.type === 'hardBreak') {
      return '\n';
    }

    // Paragraph adds double newline
    if (node.type === 'paragraph') {
      if (!node.content || node.content.length === 0) {
        return '\n\n';
      }
      const text = node.content.map(getText).join('');
      return text + '\n\n';
    }

    // Heading adds newlines
    if (node.type === 'heading') {
      const text = node.content ? node.content.map(getText).join('') : '';
      return text + '\n\n';
    }

    // List items
    if (node.type === 'listItem') {
      const text = node.content ? node.content.map(getText).join('') : '';
      return '• ' + text;
    }

    // Task list items
    if (node.type === 'taskItem') {
      const text = node.content ? node.content.map(getText).join('') : '';
      const checkbox = node.attrs?.checked ? '[x] ' : '[ ] ';
      return checkbox + text;
    }

    // Blockquote
    if (node.type === 'blockquote') {
      const text = node.content ? node.content.map(getText).join('') : '';
      return '> ' + text + '\n';
    }

    // Code block
    if (node.type === 'codeBlock') {
      const text = node.content ? node.content.map(getText).join('') : '';
      return '```\n' + text + '\n```\n\n';
    }

    // Horizontal rule
    if (node.type === 'horizontalRule') {
      return '---\n\n';
    }

    // Recursive case: has children
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(getText).join('');
    }

    return '';
  };

  return getText(content).trim();
}

/**
 * Generate HTML from Tiptap JSON
 * Useful for export and rendering outside of Tiptap
 */
export function generateHTMLFromTiptap(content: JSONContent): string {
  const extensions = [
    StarterKit,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Highlight.configure({
      multicolor: true,
    }),
    Underline,
    TextStyle,
    Color,
  ];

  return generateHTML(content, extensions);
}

/**
 * Detect note format from content
 */
export function detectNoteFormat(content: string | any): 'plaintext' | 'tiptap-json' {
  // If it's a string, it's plain text
  if (typeof content === 'string') {
    return 'plaintext';
  }

  // If it's an object with type='doc', it's Tiptap JSON
  if (content && typeof content === 'object' && content.type === 'doc') {
    return 'tiptap-json';
  }

  // Default to plain text
  return 'plaintext';
}

/**
 * Get word count from content (works with both plain text and Tiptap JSON)
 */
export function getWordCount(content: string | JSONContent): number {
  const plainText = typeof content === 'string'
    ? content
    : extractPlainText(content);

  if (!plainText || plainText.trim() === '') {
    return 0;
  }

  return plainText.trim().split(/\s+/).length;
}

/**
 * Get character count from content (works with both plain text and Tiptap JSON)
 */
export function getCharacterCount(content: string | JSONContent): number {
  const plainText = typeof content === 'string'
    ? content
    : extractPlainText(content);

  return plainText.length;
}
